import React from "react";
import { Link } from "react-router-dom";
import { Check, X, RotateCcw, BookOpen, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { gradeQuestion, computeScore, buildTicketTurns, MOOD_BADGE } from "@/utils/quiz";
import Badge from "@/components/quiz/Badge";
import PublishScore from "@/components/quiz/PublishScore";
import { getEarnedTier, BADGE_TOPICS } from "@/data/badges";

export default function QuizResults({ questions, answers, onRestart, topicKey, scoreId }) {
  const { correct: correctCount, total } = computeScore(questions, answers);
  const score = total ? Math.round((correctCount / total) * 100) : 0;
  const badgeTopic =
    topicKey && topicKey !== "mixed"
      ? BADGE_TOPICS.find((t) => t.key === topicKey)
      : null;
  const earnedTier = badgeTopic ? getEarnedTier(score) : null;

  return (
    <div className="max-w-2xl mx-auto px-6 py-12 sm:py-16">
      {/* Score header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 text-primary mb-5">
          <span className="text-2xl font-semibold">{score}%</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
          Session complete
        </h1>
        <p className="mt-2 text-muted-foreground">
          You scored {correctCount} out of {total} on graded questions.
        </p>
      </div>

      {earnedTier && badgeTopic && (
        <div className="mb-10 mx-auto max-w-md flex items-center gap-4 p-5 rounded-2xl border border-primary/20 bg-primary/5">
          <Badge topic={badgeTopic} tier={earnedTier} earned size="md" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">
              Badge earned
            </p>
            <p className="text-base font-semibold text-foreground">
              {badgeTopic.label} · {earnedTier.label}
            </p>
            <p className="text-xs text-muted-foreground">
              You scored {score}% — {earnedTier.label} tier unlocked
              ({earnedTier.threshold}%+).
            </p>
          </div>
        </div>
      )}

      {/* One-time choice: does this result go on the public leaderboard? */}
      <PublishScore scoreId={scoreId} />

      {/* Question breakdown */}
      <div className="space-y-4">
        {questions.map((q, i) => {
          const a = answers[q.id];
          const isScenario = q.type === "scenario";
          const isCorrect = gradeQuestion(q, a);

          return (
            <div
              key={q.id}
              className={cn(
                "rounded-2xl border p-5 sm:p-6",
                isScenario
                  ? "border-amber-200 bg-amber-50/40"
                  : isCorrect
                  ? "border-emerald-200 bg-emerald-50/40"
                  : "border-rose-200 bg-rose-50/40"
              )}
            >
              <div className="flex items-start gap-3">
                <span
                  className={cn(
                    "flex-shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center",
                    isScenario
                      ? "bg-amber-100 text-amber-600"
                      : isCorrect
                      ? "bg-emerald-100 text-emerald-600"
                      : "bg-rose-100 text-rose-600"
                  )}
                >
                  {isScenario ? (
                    <BookOpen className="w-3.5 h-3.5" />
                  ) : isCorrect ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <X className="w-4 h-4" />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium tracking-widest uppercase text-muted-foreground mb-1">
                    Question {i + 1}
                  </p>
                  <h3 className="text-base font-medium text-foreground leading-snug">
                    {q.question}
                  </h3>

                  {/* ---- Sequence ---- */}
                  {q.type === "sequence" && (
                    <div className="mt-4 space-y-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Your order
                        </p>
                        <ol className="mt-2 space-y-1.5">
                          {(Array.isArray(a) && a.length
                            ? a
                            : q.tiles.map((_, idx) => idx)
                          ).map((tileIdx, pos) => (
                            <li
                              key={pos}
                              className="flex items-start gap-2 text-sm text-foreground/80"
                            >
                              <span
                                className={cn(
                                  "flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold",
                                  tileIdx === pos
                                    ? "bg-emerald-100 text-emerald-600"
                                    : "bg-rose-100 text-rose-600"
                                )}
                              >
                                {tileIdx === pos ? (
                                  <Check className="w-3 h-3" />
                                ) : (
                                  <X className="w-3 h-3" />
                                )}
                              </span>
                              <span>{q.tiles[tileIdx]}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                      {!isCorrect && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">
                            Correct order
                          </p>
                          <ol className="mt-2 space-y-1.5">
                            {q.tiles.map((tile, pos) => (
                              <li
                                key={pos}
                                className="flex items-start gap-2 text-sm text-foreground/80"
                              >
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-[10px] font-semibold">
                                  {pos + 1}
                                </span>
                                <span>{tile}</span>
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ---- Compliance ---- */}
                  {q.type === "compliance" && (
                    <div className="mt-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                        Draft reply (violations highlighted)
                      </p>
                      <div className="px-4 py-3.5 rounded-lg border border-border bg-card text-sm leading-relaxed text-foreground/90">
                        {q.segments.map((seg, si) => {
                          if (!seg.error) return <span key={si}>{seg.text}</span>;
                          return (
                            <span
                              key={si}
                              className="bg-emerald-100 text-emerald-700 rounded px-1 py-0.5 font-medium"
                            >
                              {seg.text}
                            </span>
                          );
                        })}
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">
                        You highlighted{" "}
                        {Array.isArray(a) ? a.length : 0} of{" "}
                        {q.segments.filter((s) => s.error).length} violations.
                      </p>
                    </div>
                  )}

                  {/* ---- Ticket simulation ---- */}
                  {q.type === "ticket_simulation" && (
                    <div className="mt-4 space-y-2.5">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                          {a?.arc === "stress"
                            ? "Escalating · stress test"
                            : a?.arc
                            ? a.arc
                            : "ticket"}
                        </span>
                      </div>
                      {buildTicketTurns(q, a?.arc).map((r, ri) => {
                        const pick = a?.picks?.[ri];
                        const ok = pick === r.correctIndex;
                        return (
                          <div
                            key={ri}
                            className="rounded-lg border border-border bg-card p-3"
                          >
                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                              <span
                                className={cn(
                                  "text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded",
                                  MOOD_BADGE[r.mood] || MOOD_BADGE.neutral
                                )}
                              >
                                {r.mood}
                              </span>
                              {r.kind === "out_of_scope" && (
                                <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                                  Unfamiliar
                                </span>
                              )}
                              {r.kind === "mind_change" && (
                                <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">
                                  Mind change
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-foreground/80 italic">
                              "{r.message}"
                            </p>
                            <p
                              className={cn(
                                "mt-2 text-xs flex items-center gap-1.5",
                                ok ? "text-emerald-600" : "text-foreground/70"
                              )}
                            >
                              {ok ? (
                                <Check className="w-3.5 h-3.5 text-emerald-600" />
                              ) : (
                                <X className="w-3.5 h-3.5 text-rose-600" />
                              )}
                              Your reply:{" "}
                              {r.options[pick] ?? "— Not answered —"}
                            </p>
                            {!ok && (
                              <p className="mt-1 text-xs text-emerald-600">
                                Best reply: {r.options[r.correctIndex]}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* ---- Multiple choice / true false ---- */}
                  {(q.type === "multiple_choice" || q.type === "true_false") && (
                    <>
                      <div className="mt-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Your answer
                        </p>
                        <p className="mt-1 text-sm text-foreground/80">
                          {q.type === "multiple_choice"
                            ? q.options[a] ?? "— Not answered —"
                            : a === true
                            ? "True"
                            : a === false
                            ? "False"
                            : "— Not answered —"}
                        </p>
                      </div>
                      {!isCorrect && (
                        <div className="mt-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">
                            Correct answer
                          </p>
                          <p className="mt-1 text-sm text-foreground/80">
                            {q.type === "multiple_choice"
                              ? q.options[q.correctIndex]
                              : q.correctAnswer
                              ? "True"
                              : "False"}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {/* ---- Scenario ---- */}
                  {isScenario && (
                    <>
                      <div className="mt-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Your response
                        </p>
                        <p className="mt-1 text-sm text-foreground/80 whitespace-pre-wrap">
                          {a && a.trim() ? a : "— No response written —"}
                        </p>
                      </div>
                      <div className="mt-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">
                          Reference answer
                        </p>
                        <p className="mt-1 text-sm text-foreground/80 whitespace-pre-wrap">
                          {q.referenceAnswer}
                        </p>
                      </div>
                    </>
                  )}

                  {/* Explanation */}
                  <p className="mt-4 pt-3 border-t border-border text-sm text-muted-foreground italic">
                    {q.explanation}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Button
          onClick={onRestart}
          className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8"
        >
          <RotateCcw className="mr-2 w-4 h-4" />
          Start over
        </Button>
        <Link to="/leaderboard">
          <Button
            variant="outline"
            className="rounded-full border-border px-8 w-full sm:w-auto"
          >
            <Trophy className="mr-2 w-4 h-4" />
            View leaderboard
          </Button>
        </Link>
      </div>
    </div>
  );
}
